import React, { useEffect, useMemo, useState } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import backgroundImage from '../assets/images/suggesstionedit.jpg';

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().slice(0, 10);
  } catch (error) {
    return '';
  }
};

const STATUS_OPTIONS = [
  { value: 'solved', label: 'Solved' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
];

const ComplaintSolutionModal = ({ complaint, onClose, onSubmit, submitting = false, error = null }) => {
  const initialReason = useMemo(
    () => complaint?.resolutionNote ?? complaint?.solution ?? '',
    [complaint],
  );
  const initialDate = useMemo(
    () => formatDate(complaint?.resolvedAt || complaint?.resolvedDate) || formatDate(new Date()),
    [complaint],
  );
  const initialStatus = useMemo(() => {
    const status = typeof complaint?.status === 'string' ? complaint.status.toLowerCase() : 'solved';
    if (status === 'completed') {
      return 'completed';
    }
    if (status === 'pending') {
      return 'pending';
    }
    return 'solved';
  }, [complaint]);

  const [status, setStatus] = useState(initialStatus);
  const [reason, setReason] = useState(initialReason);
  const [resolvedDate, setResolvedDate] = useState(initialDate);

  useEffect(() => {
    setStatus(initialStatus);
    setReason(initialReason);
    setResolvedDate(initialDate);
  }, [initialDate, initialReason, initialStatus]);

  if (!complaint) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();

    if (typeof onSubmit === 'function') {
      onSubmit({
        id: complaint.id,
        status,
        resolutionNote: reason.trim(),
        resolvedDate: resolvedDate || formatDate(new Date()),
      });
    }
  };

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <div
      className="complaints-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="complaint-solution-title"
    >
      <div
        className="complaints-modal-panel"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="complaints-modal-surface" aria-hidden="true" />
        <button
          type="button"
          onClick={handleClose}
          className="complaints-modal-close"
          aria-label="Close"
        >
          <HiOutlineX className="h-5 w-5" aria-hidden="true" />
        </button>
        <form
          onSubmit={handleSubmit}
          className="complaints-modal-content"
        >
          <div className="text-center">
            <h2 id="complaint-solution-title" className="complaints-modal-title">
              Resolve Complaint
            </h2>
            <p className="complaints-modal-subtitle mt-2">
              Provide the solution details and resolution date to mark this complaint as solved.
            </p>
          </div>

          <div className="d-flex flex-column gap-3">
            <label className="d-flex flex-column gap-2 text-start fw-semibold text-dark">
              Status
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="form-select complaints-modal-input"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="d-flex flex-column gap-2 text-start fw-semibold text-dark">
              Reason
              <textarea
                required={status !== 'pending'}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={4}
                className="form-control complaints-modal-input complaints-modal-textarea"
                placeholder="Describe the resolution or reason for the status update"
                disabled={status === 'pending'}
              />
            </label>

            <label className="d-flex flex-column gap-2 text-start fw-semibold text-dark">
              Date
              <input
                type="date"
                required={status !== 'pending'}
                value={resolvedDate}
                onChange={(event) => setResolvedDate(event.target.value)}
                className="form-control complaints-modal-input"
                disabled={status === 'pending'}
              />
            </label>
          </div>

          {error && (
            <div className="rounded-4 border border-danger-subtle bg-danger-subtle px-4 py-3 text-sm text-danger-emphasis">
              {error}
            </div>
          )}

          <div className="d-flex flex-column flex-sm-row align-items-center justify-content-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="btn complaints-modal-primary px-4 py-2"
            >
              {submitting ? 'Updating...' : 'Update Status'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="btn complaints-modal-secondary px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComplaintSolutionModal;
